/**
 * Step Guides Content Registry
 * Centralized content for all eFleetPlan input step guides
 * 
 * Each guide includes:
 * - version: Content version (increment when updating content)
 * - title: Step title
 * - description: Main description of what the step does
 * - bullets: Array of bullet points explaining contributions to final outcome
 */

export const stepGuides = {
  routes: {
    version: 2,
    title: 'Step 1: Route Selection',
    description: 'Define your operational routes for a representative day. Each route includes the complete daily itinerary of a vehicle: Origin, stops, destination, and timing details. We will start by assuming the routes are currently served by diesel vehicles, and then assess whether switching to electric is feasible and cost-effective¹.',
    note: '¹Our analysis currently assumes a direct 1-to-1 replacement of diesel vehicles with EVs.',
    bullets: [
      'Quantifies energy needs and operational constraints per route',
      'Identifies charging opportunities and feasible schedules',
      'Establishes the foundation for EV suitability and cost analysis'
    ]
  },
  depots: {
    version: 3,
    title: 'Step 2: Depot Selection',
    description: 'Add depot locations where you may install private charging¹. You can review local grid capacity², choose charger types, and optionally include solar or battery systems for each site. Your depot setup will play a key role in the case analysis.',
    note: '¹Depot charging is included in the analysis if any route stop or destination is located within 2 miles of the depot. ²Grid capacity coverage is currently limited to Southern California Edison territory.',
    bullets: [
      'Enables on-site charging and optimal charger placement/sizing',
      'Evaluates grid capacity, interconnection, and infrastructure costs',
      'Assesses solar/battery options and TCO impact'
    ]
  },
  chargingStations: {
    version: 2,
    title: 'Step 3: Public Charging Stations',
    description: 'Select public charging stations from our database that are suitable for medium- and heavy-duty vehicles. These stations provide charging options when vehicles are away from depots¹.',
    note: '¹Currently, public charging stations are only effective if at least one route has a stop within 2 miles of the station.',
    bullets: [
      'Expands charging options for longer routes and contingencies',
      'Compares public vs private charging costs and performance',
      'Guides the optimal mix of depot and public charging arrangements'
    ]
  },
  vehicles: {
    version: 2,
    title: 'Step 4: Vehicle Selection',
    description: 'Compare diesel and electric truck configurations that fit your operation. Select¹ from our database or add custom vehicle specifications to evaluate different electrification scenarios.',
    note: '¹Each vehicle class required by your selected routes must have at least one candidate EV selected.',
    bullets: [
      'Defines specifications for candidate vehicles',
      'Compares diesel vs electric vehicle costs and TCO drivers',
      'Identifies the optimal vehicle mix for your routes'
    ]
  },
  rates: {
    version: 2,
    title: 'Step 5: Energy Rates',
    description: 'Configure time-of-use electricity rates and demand charges that affect your charging costs. Accurate rate information ensures realistic cost projections.',
    bullets: [
      'Sets electricity costs and demand charges for charging',
      'Optimizes charging schedules to reduce operational expenses',
      'Improves financial projections with realistic rate assumptions'
    ]
  },
  review: {
    version: 2,
    title: 'Step 6: Review & Optimize',
    description: 'Review all your inputs and run the analysis. This step generates your electrification strategy recommendations and cost projections.',
    bullets: [
      'Validates inputs and runs analysis',
      'Produces strategy recommendations and cost projections',
      'Delivers actionable guidance on vehicles and charging mix'
    ]
  }
};

/**
 * Get guide content for a specific step
 * @param {string} stepId - Step identifier
 * @returns {Object|null} Guide content or null if not found
 */
export const getStepGuide = (stepId) => {
  return stepGuides[stepId] || null;
};

/**
 * Get all step guides
 * @returns {Object} All step guides
 */
export const getAllStepGuides = () => {
  return stepGuides;
};

